<template>
  <div class="overlay">
    <video ref="video" autoplay muted playsinline style="z-index: -1;"></video>
    <canvas ref="videoCanvas"></canvas>
    <canvas v-show="!showMask" ref="canvas"></canvas>
    <img v-if="!frontImage" v-show="showMask" src="@/assets/MobileScan/body-shape.svg" alt="body_shape" class="body-shape" />
    <img v-else v-show="showMask" src="@/assets/MobileScan/body-shape-side.svg" alt="body_shape" class="body-shape" />
    <div class="border top-left"></div>
    <div class="border top-right"></div>
    <div class="border bottom-left"></div>
    <div class="border bottom-right"></div>
  </div>
</template>

<script>
import throttle from 'lodash/throttle';
import { wait } from '@/modules/utils';
import PoseDetector from '@/modules/PoseDetector';
import PoseUtils from '@/modules/PoseUtils';
import PoseDrawer from '@/modules/PoseDrawer';
import FPSLimiter from '@/modules/FPSLimiter';
import audioPlayer from '@/mixins/audio-player';

export default {
  mixins: [audioPlayer],
  data() {
    return {
      frontImage: null,
      sideImage: null,
      frontLandmarks: null,
      sideLandmarks: null,
      stream: null,
      drawCamera: false,

      stop: false,
      fpsLimiter: new FPSLimiter(30),
      showMask: true,
      isReady: false,
      isPoseError: false,
    };
  },
  mounted() {
    this.startCamera();
    this.$refs.video.oncanplay = () => {
      this.$emit('oncanplay', this.$refs.video);
      this.setSize(this.$refs.video.videoWidth, this.$refs.video.videoHeight);
      this.drawCamera = true;
      this.drawCameraView();
      this.start();
    }
        
    this.throttledPlayAudio = throttle(key => {
      this.stopAllAudio();
      this.playAudio(key, true)
    }, 6000, { trailing: false });

    this.errorAudioKeys = {
      1: 'ms-error-01-keep_close',
      2: 'ms-error-02-keep_far',
      3: 'ms-error-03-keep_center',
      4: 'ms-error-04-shoulder_feet',
      5: 'ms-error-05-stand_straight',
      6: 'ms-error-06-keep_front',
      7: 'ms-error-07-arm_open',
      17: 'ms-error-07-arm_open',
      27: 'ms-error-07-arm_open',
      8: 'ms-error-07-arm_open',
      18: 'ms-error-07-arm_open',
      28: 'ms-error-07-arm_open',

      9: 'ms-error-09-keep_left',
      10: 'ms-error-10-keep_arm',
      11: 'ms-error-11-keep_leg',
    }
  },
  methods: {
    startCamera() {
      navigator.mediaDevices
        .getUserMedia({
          audio: false,
          video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
        })
        .then(stream => {
          this.stream = stream;
          this.$refs.video.srcObject = stream;
        })
        .catch(e => {
          console.log(`[ERROR] startCamera failed: ${e.message}`);
        });
    },
    drawCameraView() {
      if (!this.drawCamera) return;
      const canvas = this.$refs.videoCanvas;
      const ctx = this.$refs.videoCanvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(this.$refs.video, 0, 0);
      requestAnimationFrame(() => this.drawCameraView());
    },
    takePicture(isFront = true) {
      this.stop = true;
      if (isFront) {
        this.frontLandmarks = PoseDetector.getResults()?.poseLandmarks;
      }
      else {
        this.sideLandmarks = PoseDetector.getResults()?.poseLandmarks;
      }
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      const { video } = this.$refs;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      return new Promise(resolve => {
        canvas.toBlob(
          blob => {
            try {
              const url = URL.createObjectURL(blob);
              if (isFront) this.frontImage = url;
              else this.sideImage = url;
            } catch (error) {
              console.log(`[ERROR] takePicture failed: ${error.message}`);
            }
            resolve();
          },
          'image/jpeg',
          0.95
        );
      });
    },
    resetPoseState() {
      this.showMask = true;
      this.isReady = false;
      this.isPoseError = false;
    },
    start() {
      this.stop = false;
      this.resetPoseState();

      wait(5).then(() => {
        this.fixUpdate();
      })
    },
    setSize(width, height) {
      const { canvas, videoCanvas } = this.$refs;
      if (canvas) {
        canvas.width = width;
        canvas.height = height;
      }
      if (videoCanvas) {
        videoCanvas.width = width;
        videoCanvas.height = height;
      }
    },
    playErrorSound(errorCode) {
      if (this.errorAudioKeys[errorCode]) {
        this.throttledPlayAudio(this.errorAudioKeys[errorCode]);
      }
    },
    parseLandmarks() {
      const { poseLandmarks } = PoseDetector.getResults();
      const isHumanDetected = PoseUtils.IsLandmarksInRect(poseLandmarks, 0.1, 0.9, 0, 1);

      if (!Array.isArray(poseLandmarks) || !isHumanDetected) {
        this.resetPoseState();
        return;
      }
      this.showMask = false;

      let errorCode = -1;
      let isVisible = false;
      if (!this.frontImage) {
        errorCode = PoseUtils.IsFrontPose(poseLandmarks);
        isVisible = PoseUtils.IsMainLandmarksVisible(poseLandmarks, 0.9);
      }
      else {
        errorCode = PoseUtils.IsLeftPose(poseLandmarks);
        isVisible = PoseUtils.IsRightLandmarksVisible(poseLandmarks, 0.8);
      }

      this.isReady = isVisible && errorCode === 0;
      this.isPoseError = !this.isReady;
      this.playErrorSound(errorCode);
      this.drawLandmarks(errorCode);
    },
    drawLandmarks(errorCode) {
      const { canvas } = this.$refs;
      const color = (this.isReady) ? '#03FF00' : '#D12C2D';
      const opts = { color };
      if (errorCode === 7 || errorCode === 8 || errorCode === 17 || errorCode === 18) {
        opts.color = '#03FF00';
        opts.leftArmColor = '#D12C2D';
      }
      if (errorCode === 7 || errorCode === 8 || errorCode === 27 || errorCode === 28) {
        opts.color = '#03FF00';
        opts.rightArmColor = '#D12C2D';
      }
      PoseDrawer.drawResults(PoseDetector.getResults(), canvas, opts);
    },
    fixUpdate() {
      if (this.stop) return;
      if (this.fpsLimiter.update()) {
        try {
          this.parseLandmarks();
        } catch (e) {
          this.resetPoseState();
          console.log(`[ERROR] fixUpdate failed: ${e.message}`);
        }
      }
      requestAnimationFrame(this.fixUpdate);
    },
  },
  beforeDestroy() {
    this.drawCamera = false;
    this.stop = true;
    if (this.stream) {
      const tracks = this.stream.getTracks();
      tracks.forEach(track => track.stop());
    }
  },
  watch: {
    isReady(nv) {
      this.$emit('onReady', nv);
    },
    isPoseError(nv) {
      this.$emit('onError', nv);
    },
  },
};
</script>

<style lang="scss" scoped>
.overlay {
  @apply w-full h-full relative;
  video {
    position: absolute;
    transform: scale(-1, 1);
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
  canvas, .body-shape {
    position: absolute;
    transform: scale(-1, 1);
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
  .border {
    position: absolute;
    height: 70px;
    width: 70px;
    border: 3px solid white;
  }
  .top-left {
    top: -1.5px;
    left: -1.5px;
    border-right: 0px;
    border-bottom: 0px;
  }
  .top-right {
    top: -1.5px;
    right: -1.5px;
    border-left: 0px;
    border-bottom: 0px;
  }
  .bottom-left {
    bottom: -1.5px;
    left: -1.5px;
    border-top: 0px;
    border-right: 0px;
  }
  .bottom-right {
    bottom: -1.5px;
    right: -1.5px;
    border-top: 0px;
    border-left: 0px;
  }
}
</style>
